import React, {useCallback} from 'react';
import {Button, ButtonGroup, Icon, Menu, MenuItem, Space, Text, ViewIntent} from '@mc/ui-v4';
import classNames from 'classnames';
import {EditorTool, EditorToolType, ExtendedEditorToolDefinition} from '@mc/react-mapio';
import {getToolInfo} from '../../helpers/ToolsServiceMapper';
import * as IconSpec from '@mc/react-ui-icons';

type Props = {
    classname?: string;
    onClick: (tool: any) => void;
    disabled?: boolean;
    intent?: ViewIntent;
    activeTool: EditorToolType;
    toolsEnabled?: string[];
    items: (ExtendedEditorToolDefinition | EditorTool)[];
    horizontal?: boolean;
};

export const ToolbarDropdown: React.FC<Props> = ({intent = 'primary', classname, onClick, disabled, items, activeTool, toolsEnabled, horizontal}) => {
    const getFirstOrActiveItem = useCallback(() => {
        if (!items || items.length === 0) return null;
        for (const item of items) {
            const toolInfo = getToolInfo(item);
            if (toolInfo.type === activeTool) {
                return toolInfo;
            }
        }
        return getToolInfo(items[0]);
    }, [items, activeTool]);

    const isEnabledForTool = useCallback(
        toolInfo => {
            const filterActive = Array.isArray(toolsEnabled) && toolsEnabled.length > 0;
            if (!filterActive) return true;
            if (!toolInfo?.type) return false;
            return toolsEnabled.includes(toolInfo.type);
        },
        [toolsEnabled],
    );

    const isActive = useCallback(
        (selected, toolInfo) => {
            if (activeTool && toolInfo.type) {
                return activeTool === toolInfo.type;
            } else return selected?.id ? selected.id === toolInfo.id : selected?.type === toolInfo?.type;
        },
        [activeTool],
    );

    const getToolIntent = useCallback(
        (localEnabled: boolean, toolType: EditorToolType): ViewIntent => {
            if (!localEnabled) return 'inactive';
            return toolType === EditorToolType.DELETE || toolType === EditorToolType.CUTOUT_REMOVE ? 'danger' : intent;
        },
        [intent],
    );

    const firstOrActiveItem = getFirstOrActiveItem();
    const icon = firstOrActiveItem ? IconSpec[firstOrActiveItem.icon] : undefined;

    return (
        <>
            {horizontal ? (
                <Space>
                    {items.map(item => {
                        const toolInfo = getToolInfo(item);
                        const localEnabled = isEnabledForTool(toolInfo);
                        const enabled = !disabled && localEnabled;

                        const active = isActive(firstOrActiveItem, toolInfo);
                        return (
                            <Button
                                key={toolInfo.id ?? toolInfo.type}
                                disabled={!enabled}
                                inheritColor
                                style={'link'}
                                tooltip={toolInfo.name}
                                className={classNames('toolbar-button vertical', classname, {['active']: active})}
                                danger={toolInfo.type === EditorToolType.DELETE}
                                onClick={() => enabled && onClick(toolInfo)}
                            >
                                <div className={'d-flex flex-column align-items-center'}>
                                    <Icon intent={getToolIntent(enabled, toolInfo.type)} icon={IconSpec[toolInfo.icon]} size={'lg'} />
                                </div>
                            </Button>
                        );
                    })}
                </Space>
            ) : (
                <ButtonGroup>
                    {/* Potřebuju být schopný vybrat i ten nástroj, co rozbaluje menu. */}
                    <Button
                        disabled={disabled || !isEnabledForTool(firstOrActiveItem)}
                        inheritColor
                        style={'link'}
                        className={classNames('toolbar-button vertical', classname, {['active']: isActive(firstOrActiveItem, firstOrActiveItem)})}
                        onClick={() => onClick(firstOrActiveItem)}
                        icon={<Icon intent={getToolIntent(isEnabledForTool(firstOrActiveItem), firstOrActiveItem?.type!)} icon={icon} size={'lg'} />}
                    >
                        {firstOrActiveItem?.name}
                    </Button>
                    <Button
                        disabled={disabled}
                        inheritColor
                        style={'link'}
                        className={classNames('toolbar-button vertical', classname, {['active']: isActive(firstOrActiveItem, firstOrActiveItem)})}
                        dropdown
                        popupProps={{position: 'bottom right'}}
                        menu={
                            <Menu>
                                {items.map(item => {
                                    const toolInfo = getToolInfo(item);
                                    if (firstOrActiveItem?.type === toolInfo.type) return;
                                    const localEnabled = isEnabledForTool(toolInfo);
                                    const enabled = !disabled && localEnabled;
                                    return (
                                        <MenuItem
                                            key={toolInfo.id ?? toolInfo.type}
                                            icon={<Icon icon={IconSpec[toolInfo.icon]} intent={getToolIntent(enabled, toolInfo.type)} size={'lg'} />}
                                            onClick={() => enabled && onClick(toolInfo)}
                                            disabled={!enabled}
                                            title={toolInfo.name}
                                            intent={
                                                toolInfo.type === EditorToolType.DELETE || toolInfo.type === EditorToolType.CUTOUT_REMOVE ? 'danger' : 'default'
                                            }
                                            text={
                                                <Text className="pl-1" intent={getToolIntent(localEnabled, toolInfo.type)}>
                                                    {toolInfo.name}
                                                </Text>
                                            }
                                        />
                                    );
                                })}
                            </Menu>
                        }
                    />
                </ButtonGroup>
            )}
        </>
    );
};
